import * as React from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { ChevronRight } from "lucide-react";
import { track } from "@/lib/track";
import { startViewTransition } from "@/lib/view-transition";
import { useLocalizedHref } from "@/hooks/useLocalizedHref";
import { RotatingWord } from "@/components/CtaBand";

export function FinalCTA() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const localizedHref = useLocalizedHref();
  const fitHref = localizedHref("/fit");
  const [isVisible, setIsVisible] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  const goToFit = (e: React.MouseEvent<HTMLAnchorElement>) => {
    track("fit_click", { source: "finalcta" });
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    startViewTransition(async () => {
      await navigate({ to: fitHref });
    });
  };

  React.useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setIsVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.1 },
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={ref} id="cta" className="frame relative bg-ink text-page-bg">
      <div
        aria-hidden="true"
        className="absolute -top-40 -right-40 h-[36rem] w-[36rem] rounded-full"
        style={{
          background: "radial-gradient(circle, rgba(239,77,35,0.18), transparent 70%)",
        }}
      />
      <div
        aria-hidden="true"
        className="absolute -bottom-20 -left-20 h-[28rem] w-[28rem] rounded-full bg-accent/5 blur-3xl"
      />

      <div
        aria-hidden="true"
        className="absolute top-12 right-12 z-10 hidden items-center justify-center md:flex lg:top-16 lg:right-20"
      >
        <div
          className="flex h-[140px] w-[140px] flex-col items-center justify-center rounded-full border-[1.5px] border-accent/70 text-center leading-none text-page-bg/95"
          style={{ transform: "rotate(-8deg)" }}
        >
          <span className="mono text-[11px] tracking-[0.18em] text-page-bg/65 uppercase">
            {t("landing.finalCta.sealFree")}
          </span>
          <span className="serif-italic mt-1.5 text-[28px] leading-none text-accent">
            {t("landing.finalCta.sealDuration")}
          </span>
          <span className="mono mt-1.5 text-[11px] tracking-[0.18em] text-page-bg/65 uppercase">
            {t("landing.finalCta.sealCall")}
          </span>
        </div>
      </div>

      <div className="container-narrow section-pad relative">
        <span className="eyebrow">
          <span className="num text-page-bg/40">09 —</span> {t("landing.finalCta.eyebrow")}
        </span>
        <h2
          className={cn(
            "mt-4 text-[clamp(40px,6vw,84px)] leading-[1.02] font-medium tracking-[-0.035em]",
            "transition-all duration-700",
            isVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0",
          )}
        >
          {t("landing.finalCta.titleLead")}{" "}
          <span className="serif-italic whitespace-nowrap text-accent">
            <RotatingWord
              words={t("landing.finalCta.words", { returnObjects: true }) as string[]}
            />
          </span>
        </h2>

        <p className="mt-6 max-w-[58ch] text-[clamp(15px,1.4vw,18px)] leading-[1.55] text-page-bg/70">
          {t("landing.finalCta.lede")}
        </p>

        <div className="mt-9 flex flex-wrap items-center gap-3">
          <a
            href="https://www.cal.eu/andreacab/holymoly-free-call?user=andreacab"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => track("cta_click", { source: "finalcta" })}
            className="inline-flex items-center gap-2 rounded-full bg-page-bg py-2 pr-2 pl-5 text-[14px] font-medium tracking-tight text-ink transition-colors hover:bg-white"
          >
            {t("landing.finalCta.bookCall")}
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-ink/10">
              <ChevronRight className="h-3.5 w-3.5" strokeWidth={2.5} />
            </span>
          </a>
          <Link
            to={fitHref}
            onClick={goToFit}
            className="inline-flex items-center gap-1.5 rounded-full border border-page-bg/30 bg-page-bg/10 px-4 py-2 text-[14px] font-medium tracking-tight text-page-bg backdrop-blur transition-colors hover:bg-page-bg/15"
          >
            {t("landing.finalCta.fitCta")}
          </Link>
          <a
            href="mailto:hello@holymoly.dev"
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[14px] font-medium tracking-tight text-page-bg transition-colors hover:bg-page-bg/5"
          >
            {t("landing.finalCta.emailCta")}
          </a>
        </div>
      </div>
    </section>
  );
}
